import {
  Breadcrumb,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../axios";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { ReactComponent as ArchiveRestore } from "../../image/ArchiveRestore.svg";
import SearchIcon from "../../image/SearchIcon.svg";
import "../../styles/Main.scss";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";

const { Search } = Input;

const ArchiveCompanyOnboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [archiveCompanyOnboarding, setArchiveCompanyOnboarding] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Initialize state variables to manage table pagination, sorting, and search
  const [state, setState] = useState({
    page: 1,                 // Current page number
    ordering: "id",          // Default sorting order
    pagesize: 10,            // Number of records per page
    search: "",              // Search query string
    total: 0,                // Total number of records
  });

  // useEffect hook to fetch the archived company onboarding list whenever state, reload, or t changes
  useEffect(() => {
    const getCompanyOnboardingArchiveList = async () => {
      setLoading(true); // Set loading state to true before fetching data

      try {
        // Fetch the company archive list based on current state parameters
        let apiResponse = await getRequest("company-archive/", state);
        if (apiResponse.status) {
          // Reverse the results to display the latest records first
          const sortedData = apiResponse.data.results.reverse();
          setArchiveCompanyOnboarding(sortedData); // Update the archived list in state
          settotalCount(apiResponse.data.count);   // Update the total record count
        } else {
          // Show error notification if the fetch fails
          ErrorNotificationMsg(t("error_in_archive_list"));
        }
        setLoading(false); // Set loading state to false after data is fetched
      } catch (error) {
        setLoading(false); // Set loading state to false in case of error
      }
    };

    getCompanyOnboardingArchiveList(); // Fetch archive list on component mount or dependency update
  }, [state, reload, t]);

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    // Get sorting order based on the column key and sort order
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);

    // Update state with new pagination, page size, and ordering information
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });

  };


  // Convert sorting parameters into a format that the API can understand
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id"; // Default sorting by ID

    // Map column key and order to the corresponding API sorting value
    if (sortField === "name" && sortOrder === "ascend") {
      sortingEnumKey = "name";
    }
    if (sortField === "name" && sortOrder === "descend") {
      sortingEnumKey = "-name";
    }
    if (sortField === "first_name" && sortOrder === "ascend") {
      sortingEnumKey = "first_name";
    }
    if (sortField === "first_name" && sortOrder === "descend") {
      sortingEnumKey = "-first_name";
    }
    if (sortField === "designation" && sortOrder === "ascend") {
      sortingEnumKey = "designation";
    }
    if (sortField === "designation" && sortOrder === "descend") {
      sortingEnumKey = "-designation";
    }
    if (sortField === "email" && sortOrder === "ascend") {
      sortingEnumKey = "email";
    }
    if (sortField === "email" && sortOrder === "descend") {
      sortingEnumKey = "-email";
    }
    if (sortField === "phone" && sortOrder === "ascend") {
      sortingEnumKey = "phone";
    }
    if (sortField === "phone" && sortOrder === "descend") {
      sortingEnumKey = "-phone";
    }
    if (sortField === "address_line_1" && sortOrder === "ascend") {
      sortingEnumKey = "address_line_1";
    }
    if (sortField === "address_line_1" && sortOrder === "descend") {
      sortingEnumKey = "-address_line_1";
    }

    if (sortField === "address_line_2" && sortOrder === "ascend") {
      sortingEnumKey = "address_line_2";
    }
    if (sortField === "address_line_2" && sortOrder === "descend") {
      sortingEnumKey = "-address_line_2";
    }

    if (sortField === "city" && sortOrder === "ascend") {
      sortingEnumKey = "city";
    }
    if (sortField === "city" && sortOrder === "descend") {
      sortingEnumKey = "-city";
    }

    if (sortField === "state" && sortOrder === "ascend") {
      sortingEnumKey = "state";
    }
    if (sortField === "state" && sortOrder === "descend") {
      sortingEnumKey = "-state";
    }

    if (sortField === "pincode_number" && sortOrder === "ascend") {
      sortingEnumKey = "pincode__pincode_number";
    }
    if (sortField === "pincode_number" && sortOrder === "descend") {
      sortingEnumKey = "-pincode__pincode_number";
    }

    if (sortField === "website" && sortOrder === "ascend") {
      sortingEnumKey = "website";
    }
    if (sortField === "website" && sortOrder === "descend") {
      sortingEnumKey = "-website";
    }
    if (sortField === "no_of_employees" && sortOrder === "ascend") {
      sortingEnumKey = "no_of_employees";
    }
    if (sortField === "no_of_employees" && sortOrder === "descend") {
      sortingEnumKey = "-no_of_employees";
    }

    if (sortField === "company_pan" && sortOrder === "ascend") {
      sortingEnumKey = "company_pan";
    }
    if (sortField === "company_pan" && sortOrder === "descend") {
      sortingEnumKey = "-company_pan";
    }

    if (sortField === "gst_no" && sortOrder === "ascend") {
      sortingEnumKey = "gst_no";
    }
    if (sortField === "gst_no" && sortOrder === "descend") {
      sortingEnumKey = "-gst_no";
    }
    if (sortField === "cin_no" && sortOrder === "ascend") {
      sortingEnumKey = "cin_no";
    }
    if (sortField === "cin_no" && sortOrder === "descend") {
      sortingEnumKey = "-cin_no";
    }
    if (sortField === "sector_name" && sortOrder === "ascend") {
      sortingEnumKey = "sector_name";
    }
    if (sortField === "sector_name" && sortOrder === "descend") {
      sortingEnumKey = "-sector_name";
    }

    return sortingEnumKey;
  };

  // Debounce the search input to reduce the frequency of API calls
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch); // Call onSearch with the debounced value
  };

  // Update the search query in state when the search input changes
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };


  // Debounce function to delay the API call for searching by 1 second
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value); // Call the onSearch function with the debounced value
  }, 1000);


  // Configuration for table row selection behavior
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      // Handle row selection change
    },
    onSelect: (record, selected, selectedRows) => {
      // Update selected rows array based on whether the row is selected or deselected
      const updatedSelectedRowKeys = selected ? [...selectedRow, record.id] : selectedRow.filter(key => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys); // Update selected rows in state
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      // Update selected rows array when selecting or deselecting all rows
      const updatedSelectedRowKeys = selected ? [...selectedRow, ...changeRows.map(row => row.id)] : selectedRow.filter(key => !changeRows.find(row => row.id === key));
      setSelectedRow(updatedSelectedRowKeys); // Update selected rows in state
    },
    selectedRowKeys: selectedRow, // Keep track of selected row keys
  };

  // Handle the restoration of archived companies
  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id); // Add the ID if no rows are selected
    }
    try {
      // Send a request to restore the selected company or companies
      let apiResponse = await postRequest("company-archive/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        // Show success notification and reset the selected rows and reload state
        SuccessNotificationMsg(t("company_onboarding_restore_successfully1"));
        setReload(!reload); // Toggle reload state to refresh the table
        setSelectedRow([]); // Clear selected rows after restore
        navigate("/my-company"); // Redirect to the company page
      } else {
        // Show error notification if the restore fails
        ErrorNotificationMsg(t("error_in_restore_company_onboarding"));
      }
    } catch (error) {
      // Show error notification in case of an exception
      ErrorNotificationMsg(error);
    }
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      width: 150
    },
    {
      title: "Person Name",
      dataIndex: "first_name",
      key: "first_name",
      sorter: true,
      width: 160
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      sorter: true,
      width: 140
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      width: 190
    },
    {
      title: "Contact Number",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => <>+{phone} </>,
      sorter: true,
      width: 160
    },
    {
      title: "Address Line 1",
      dataIndex: "registered_business_address_area",
      key: "registered_business_address_area",
      sorter: true,
      width: 160
    },
    {
      title: "City",
      dataIndex: "registered_business_address_city",
      key: "registered_business_address_city",
      sorter: true,
      width: 160
    },
    {
      title: "State",
      dataIndex: "registered_business_address_state",
      key: "registered_business_address_state",
      sorter: true,
      width: 160
    },

    {
      title: "Pincode",
      dataIndex: "registered_business_address_pincode_number",
      key: "registered_business_address_pincode_number",
      sorter: true,
      width: 120
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      sorter: true,
      width: 160

    },
    {
      title: "Number Of Employee",
      dataIndex: "no_of_employees",
      key: "no_of_employees",
      sorter: true,
      width: 200

    },

    {
      title: "PAN Number",
      dataIndex: "company_pan",
      key: "company_pan",
      sorter: true,
      width: 160

    },
    {
      title: "GST Number",
      dataIndex: "gst_no",
      key: "gst_no",
      sorter: true,
      width: 160

    },
    {
      title: "CIN Number",
      dataIndex: "cin_no",
      key: "cin_no",
      sorter: true,
      width: 140

    },
    {
      title: "Sector Name",
      dataIndex: "sector_name",
      key: "sector_name",
      sorter: true,
      width: 140
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          <Tooltip
            color={colors}
            placement="right"
            title={
              <span style={{ color: "#fff" }}>
                Restore
              </span>
            }
          >
            <Popconfirm
              className="action"
              title={t("pop_restore_title")}
              okText={t("pop_ok")}
              cancelText={t("pop_cancel")}
              onConfirm={() => handleRestore(record.id)}
            >
              <ArchiveRestore className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip
              color={colors}
              placement="right"
              title={
                <span style={{ color: "#fff" }}>
                  Archived entries retain historical data and can be reinstated
                  if necessary.
                </span>
              }
            >
              <h3>{t("archived_company_onboarding")}</h3>
            </Tooltip>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/company-onboarding">{t("companyOnboarding")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {t("archived_company_onboarding")}{" "}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>

          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <div className="tablewrp">
          <Table
            scroll={{
              x: 2600,
            }}
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={archiveCompanyOnboarding}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
              locale: {
                items_per_page: "Items / Page", // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
          </div>
          <div className="FormFooter Container">
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("pl_back_to_list")}
              onClick={() => navigate(`/company-onboarding`)}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default ArchiveCompanyOnboarding;
